import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import ProducerListingModal from "../components/ProducerListingModal";

const STATUS_STYLES = {
  draft: "bg-muted text-ink2 border-line",
  pending_review: "bg-ink text-white border-ink",
  needs_info: "bg-danger/10 text-danger border-danger/40",
  approved: "bg-leaf/10 text-leaf border-leaf/40",
  rejected: "bg-danger/10 text-danger border-danger/40",
};

const fmtUsd = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const fmtTons = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} tCO₂`;

export default function Producer() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // listing being edited/created; {} for new

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/producer/listings");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load listings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const gateBlocked = user && user.role !== "producer" && user.role !== "admin";

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tag mb-4">Producer Portal · UCS</div>
          <h1 className="font-serif text-5xl tracking-tight">My credit listings</h1>
          <p className="text-ink2 mt-3 max-w-2xl text-sm">
            Launch new UCS units, attach verifier documentation, and track compliance review status.
          </p>
        </div>
        {!gateBlocked && (
          <button
            data-testid="new-listing-btn"
            onClick={() => setEditing({})}
            className="px-5 py-3 border border-ink bg-ink text-white text-[11px] font-mono uppercase tracking-wider hover:bg-white hover:text-ink"
          >
            + New listing
          </button>
        )}
      </section>

      {gateBlocked && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
          <div className="border border-danger/40 bg-danger/5 text-danger p-6 text-sm">
            Producer-only area. Register a new account and choose "UCS Producer" to submit listings.
          </div>
        </div>
      )}

      {!gateBlocked && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="producer-listings">
          {loading && <div className="py-12 text-center text-ink2 font-mono text-sm">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="border border-dashed border-line bg-surface p-10 text-center">
              <div className="font-serif text-2xl mb-2">No listings yet</div>
              <div className="text-ink2 text-sm mb-6">Click "New listing" above to draft your first UCS credit unit.</div>
              <button
                onClick={() => setEditing({})}
                className="px-5 py-3 border border-ink bg-ink text-white text-[11px] font-mono uppercase tracking-wider"
                data-testid="new-listing-btn-empty"
              >
                Start a listing →
              </button>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {items.map((it) => (
                <ListingCard
                  key={it.id}
                  listing={it}
                  onOpen={() => setEditing(it)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {editing && (
        <ProducerListingModal
          listing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </Layout>
  );
}

function ListingCard({ listing, onOpen }) {
  const style = STATUS_STYLES[listing.status] || STATUS_STYLES.draft;
  const activeDocs = (listing.documents || []).filter((d) => !d.is_deleted).length;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`listing-card-${listing.id}`}
      className="text-left border border-line bg-surface p-5 hover:border-ink hover:bg-bg/40 transition-colors"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="font-serif text-xl">{listing.name}</div>
        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${style}`}>
          {listing.status.replace("_", " ")}
        </span>
      </div>
      <div className="mt-2 text-[12px] font-mono text-ink2">{listing.region} · {listing.methodology} · vintage {listing.vintage}</div>
      <div className="mt-4 grid grid-cols-3 gap-3 text-[11px] font-mono">
        <div>
          <div className="text-ink2 uppercase">Volume</div>
          <div className="text-ink mt-1">{fmtTons(listing.tons_available)}</div>
        </div>
        <div>
          <div className="text-ink2 uppercase">Ask price</div>
          <div className="text-ink mt-1">{fmtUsd(listing.ask_price_usd_per_ton)}/t</div>
        </div>
        <div>
          <div className="text-ink2 uppercase">Docs</div>
          <div className="text-ink mt-1">{activeDocs} attached</div>
        </div>
      </div>
      {listing.review_notes && (
        <div className="mt-3 text-[11px] font-mono text-ink2 border-l-2 border-line pl-2">
          Reviewer: {listing.review_notes}
        </div>
      )}
    </button>
  );
}
